import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { useScopedPath } from "@/lib/scoped";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users, Plus, Ticket, Copy, MessageCircle, Send, ChevronRight } from "lucide-react";

const APP_NAME = "Dugsy's Football Challenge";
const MAX_LEAGUES_PER_OWNER = 3;  // mirrors backend MAX_LEAGUES_PER_OWNER

function buildInviteMessage({ userName, joinUrl, inviteCode }) {
  const safeName = (userName || "A friend").trim() || "A friend";
  return `${safeName} invites you to join a private league on ${APP_NAME}. Follow the link to ${joinUrl} and enter the code: ${inviteCode}`;
}

function copy(text, ok = "Copied") {
  if (typeof navigator !== "undefined" && navigator.clipboard) {
    navigator.clipboard.writeText(text).then(
      () => toast.success(ok),
      () => toast.error("Couldn't copy — copy it manually"),
    );
  }
}

export default function LeaguesPage() {
  const scope = useScopedPath();
  const { user } = useAuth();
  const [createName, setCreateName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [myLeagues, setMyLeagues] = useState([]);
  const [loadingMine, setLoadingMine] = useState(true);

  // Shareable invite URL — slug-aware so links dropped in WhatsApp / SMS
  // land the new player on the same branded environment the sender was in.
  const joinUrl = (typeof window !== "undefined" ? window.location.origin : "") + scope("/leagues");

  const loadMine = useCallback(async () => {
    setLoadingMine(true);
    try {
      const { data } = await api.get("/leagues/mine");
      setMyLeagues(Array.isArray(data) ? data : []);
    } catch (err) {
      // Soft-fail: if the read errors we just hide the section
      setMyLeagues([]);
    } finally {
      setLoadingMine(false);
    }
  }, []);

  useEffect(() => { loadMine(); }, [loadMine]);

  const ownedLeagues = myLeagues.filter((l) => l.is_owner);
  const joinedLeagues = myLeagues.filter((l) => !l.is_owner);
  const atCreateCap = ownedLeagues.length >= MAX_LEAGUES_PER_OWNER;

  const create = async (e) => {
    e.preventDefault();
    if (!createName.trim()) {
      toast.error("Give your league a name");
      return;
    }
    if (atCreateCap) {
      toast.error(`You can only own ${MAX_LEAGUES_PER_OWNER} mini-leagues at a time.`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/leagues", { name: createName.trim() });
      toast.success(`League "${data.name}" created — share the invite below`);
      setCreateName("");
      await loadMine();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const join = async (e) => {
    e.preventDefault();
    if (!joinCode.trim()) {
      toast.error("Paste the invite code");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/leagues/join", { invite_code: joinCode.trim().toUpperCase() });
      toast.success(`Joined "${data.name}" — pick it from The League → My leagues`);
      setJoinCode("");
      await loadMine();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="mb-10">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 inline-flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" /> Private leagues
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-zinc-950 mt-1">
            Your leagues.
          </h1>
          <p className="text-base text-zinc-500 mt-3 max-w-2xl">
            Run a mini-league with your friends — start one and share the invite code, or join one
            someone shared with you. Each mini-league holds up to <strong>20 members</strong>, and you
            can own up to <strong>3 leagues</strong> per season. League standings only count predictions
            made in the current season.
          </p>
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500 mt-3" data-testid="leagues-today-label">
            <span className="font-semibold text-zinc-950 tabular-nums">{new Date().toLocaleDateString("en-GB")}</span>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
          <form onSubmit={create} className="border border-zinc-200 rounded-sm bg-white p-6" data-testid="create-league-form">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-1.5">
              <Plus className="h-3 w-3" /> Create a new league
            </p>
            <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-950 mt-1">Start a league.</h2>
            <Label className="mt-4 block text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">League name</Label>
            <Input
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              data-testid="league-name-input"
              placeholder="The Office Banter League"
              className="mt-1 h-11 rounded-sm border-zinc-300"
              disabled={atCreateCap}
            />
            <Button
              type="submit"
              disabled={busy || atCreateCap}
              data-testid="create-league-submit"
              className="mt-4 h-11 px-5 rounded-sm bg-[#0038FF] hover:bg-[#002BE6] text-white font-semibold disabled:opacity-50"
            >
              {busy ? "Creating…" : "Create league"}
            </Button>
            {!loadingMine && (
              <p
                data-testid="create-cap-hint"
                className={`mt-3 text-xs ${atCreateCap ? "text-rose-700 font-semibold" : "text-zinc-500"}`}
              >
                {atCreateCap
                  ? `You've hit the ${MAX_LEAGUES_PER_OWNER}-league cap for this season. Delete one of your leagues to make room.`
                  : `You own ${ownedLeagues.length} of ${MAX_LEAGUES_PER_OWNER} leagues this season.`}
              </p>
            )}
          </form>

          <form onSubmit={join} className="border border-zinc-200 rounded-sm bg-white p-6" data-testid="join-league-form">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-1.5">
              <Ticket className="h-3 w-3" /> Join with code
            </p>
            <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-950 mt-1">Got an invite?</h2>
            <Label className="mt-4 block text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Invite code</Label>
            <Input
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              data-testid="join-league-input"
              placeholder="ABC1234"
              maxLength={20}
              className="mt-1 h-11 rounded-sm border-zinc-300 font-mono tracking-wider uppercase"
            />
            <Button
              type="submit"
              disabled={busy}
              data-testid="join-league-submit"
              variant="outline"
              className="mt-4 h-11 px-5 rounded-sm"
            >
              {busy ? "Joining…" : "Join league"}
            </Button>
          </form>
        </div>

        {/* OWNED LEAGUES — persistent invite section so owners can re-invite any time */}
        <section className="mb-12" data-testid="owned-leagues-section">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 mb-1">Your invites.</h2>
          <p className="text-sm text-zinc-500 mb-5">
            Leagues you run this season. Re-share these invites whenever you want to add more people.
          </p>

          {loadingMine ? (
            <p className="text-sm text-zinc-500" data-testid="owned-leagues-loading">Loading your leagues…</p>
          ) : ownedLeagues.length === 0 ? (
            <div className="border border-dashed border-zinc-300 rounded-sm p-8 text-center" data-testid="owned-leagues-empty">
              <p className="text-sm text-zinc-500">
                You haven&apos;t created a mini-league yet. Use the form above to start one — invite up to 20 people per league.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {ownedLeagues.map((lg) => {
                const inviteMsg = buildInviteMessage({
                  userName: user?.name || user?.email,
                  joinUrl,
                  inviteCode: lg.invite_code,
                });
                const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(inviteMsg)}`;
                const cap = lg.member_cap || 20;
                const isFull = lg.is_full || lg.member_count >= cap;
                return (
                  <article
                    key={lg.id}
                    data-testid={`owned-league-card-${lg.id}`}
                    className="border border-zinc-200 rounded-sm bg-white p-6"
                  >
                    <div className="flex items-start justify-between flex-wrap gap-3">
                      <div className="min-w-0">
                        <h3
                          data-testid={`owned-league-name-${lg.id}`}
                          className="font-heading text-xl font-bold tracking-tight text-zinc-950"
                        >
                          {lg.name}
                        </h3>
                        <p className="text-xs text-zinc-500 mt-1">
                          <span
                            data-testid={`owned-league-member-count-${lg.id}`}
                            className={isFull ? "text-rose-700 font-semibold" : "tabular-nums"}
                          >
                            {lg.member_count}/{cap} members
                          </span>
                          {isFull && " · full"}
                        </p>
                      </div>
                      <Link
                        to={`/leagues/${lg.id}`}
                        data-testid={`owned-league-manage-link-${lg.id}`}
                        className="inline-flex items-center text-xs font-semibold text-[#0038FF] hover:text-blue-800 mt-1"
                      >
                        Manage <ChevronRight className="h-3 w-3 ml-0.5" />
                      </Link>
                    </div>

                    {/* Invite code chip */}
                    <div className="mt-4 inline-flex items-center gap-3 px-3 py-2 rounded-sm border border-zinc-200 bg-zinc-50 flex-wrap">
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Invite code</span>
                      <code
                        data-testid={`owned-league-code-${lg.id}`}
                        className="font-mono text-lg tracking-[0.25em] font-bold text-zinc-950"
                      >
                        {lg.invite_code}
                      </code>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => copy(lg.invite_code, "Code copied")}
                        data-testid={`owned-league-copy-code-btn-${lg.id}`}
                        className="h-8 px-2.5 rounded-sm border-zinc-300 text-xs inline-flex items-center gap-1"
                        disabled={isFull}
                      >
                        <Copy className="h-3 w-3" /> Copy code
                      </Button>
                    </div>

                    {/* Pre-built message preview */}
                    <div className="mt-4">
                      <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Invite message</Label>
                      <div
                        data-testid={`owned-league-message-${lg.id}`}
                        className="mt-1 p-3 rounded-sm border border-zinc-200 bg-white text-sm text-zinc-800 leading-relaxed whitespace-pre-wrap break-words"
                      >
                        {inviteMsg}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-4 flex items-center gap-2 flex-wrap">
                      <Button
                        type="button"
                        onClick={() => copy(inviteMsg, "Invite message copied — paste it into WhatsApp, iMessage, email…")}
                        data-testid={`owned-league-copy-msg-btn-${lg.id}`}
                        disabled={isFull}
                        className="h-10 px-4 rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white inline-flex items-center gap-2 disabled:opacity-50"
                      >
                        <Send className="h-4 w-4" /> Copy message
                      </Button>
                      {isFull ? (
                        <span
                          data-testid={`owned-league-full-note-${lg.id}`}
                          className="text-xs text-rose-700 font-semibold"
                        >
                          League full — invites paused
                        </span>
                      ) : (
                        <a
                          href={whatsappUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`owned-league-whatsapp-btn-${lg.id}`}
                          className="h-10 px-4 rounded-sm bg-[#25D366] hover:bg-[#1FB358] text-white inline-flex items-center gap-2 font-semibold text-sm"
                        >
                          <MessageCircle className="h-4 w-4" /> Share to WhatsApp
                        </a>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        {/* JOINED LEAGUES — compact list pointing at the detail page */}
        {!loadingMine && joinedLeagues.length > 0 && (
          <section className="mb-12" data-testid="joined-leagues-section">
            <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-950 mb-3">Leagues you&apos;ve joined.</h2>
            <ul className="border border-zinc-200 rounded-sm divide-y divide-zinc-100">
              {joinedLeagues.map((lg) => (
                <li key={lg.id} className="flex items-center justify-between px-5 py-3" data-testid={`joined-league-row-${lg.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-950 truncate">{lg.name}</p>
                    <p className="text-xs text-zinc-500">
                      Run by {lg.owner_name} · {lg.member_count}/{lg.member_cap || 20} members
                    </p>
                  </div>
                  <Link
                    to={`/leagues/${lg.id}`}
                    className="text-xs font-semibold text-[#0038FF] hover:text-blue-800 inline-flex items-center"
                  >
                    View <ChevronRight className="h-3 w-3 ml-0.5" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <p className="text-sm text-zinc-500" data-testid="leagues-moved-note">
          Looking for full standings? Open <Link to={scope("/leaderboard")} className="font-semibold text-[#0038FF] underline">The League</Link> and pick a league from <strong>My leagues</strong> to see it scored against the current season.
        </p>
      </main>
    </div>
  );
}
